/** The one place that decides what the map's background is.
 *
 * Four components draw maps — LiveMap (dispatcher), SitePinPicker (client
 * portal), OrderRouteMap and TruckRouteSection (order inspector) — and until
 * this module they each carried their own copy of the tile URL. Four copies
 * is how the CARTO migration below turned into four identical edits, and it
 * is why the URL lives here now rather than next to each `L.map()`.
 *
 * WHY NOT CARTO, WHICH THIS REPLACED
 *
 * `{s}.basemaps.cartocdn.com/dark_all` served this app's background for
 * free until CARTO started requiring an API key: the tiles kept arriving,
 * stamped diagonally with "API KEY REQUIRED". CARTO is retiring its raster
 * basemaps, so a key would have bought months, not a fix.
 *
 * Every keyless raster replacement checked in September 2026 was the same
 * trap at a different stage — Stadia/Stamen already wants registration and a
 * domain allowlist, Esri's keyless Dark Gray Canvas is marked legacy and no
 * longer updated, and OSM's own tiles are light-themed and their usage
 * policy does not want this kind of traffic. Picking one would have
 * scheduled this same evening for a few months out.
 *
 * WHY A FILE, SERVED BY US
 *
 * So the basemap became the fourth thing built from the Lithuania OSM
 * extract this deployment already downloads three times over, after OSRM's
 * routing graph, Valhalla's truck tiles and Nominatim's address index. It is
 * a single .pmtiles archive that Caddy serves as a static file — measured at
 * 326 MiB, against 6.09 GB for Nominatim and ~600 MB for each routing
 * engine, which makes the map the LIGHTEST of the four. Nothing runs: no
 * container, no database, no daemon, no memory. See
 * deploy/basemap/prepare.sh for how it is built and how often it needs
 * rebuilding (rarely — roads move far more slowly than addresses do, and
 * unlike Nominatim this has no replication feed to keep current).
 *
 * It also lets the browser stop talking to anyone but this origin. app.py's
 * CSP says every outbound call is made by the server and that a browser
 * reaching a third party directly would be a bug; while the basemap came
 * from a CDN, `img-src` had to carve out two exceptions to that. It no
 * longer does.
 */
import { leafletLayer } from 'protomaps-leaflet';
import { PMTiles } from 'pmtiles';

/** Same-origin, so `connect-src 'self'` covers it with no CSP exception.
 *  Caddy serves this path from deploy/basemap/ — see that Caddyfile block;
 *  protomaps-leaflet fetches byte ranges out of the archive rather than
 *  downloading all 326 MiB, which is the whole point of the format. */
export const BASEMAP_URL = '/basemap/lithuania.pmtiles';

/** OpenStreetMap is the data; Protomaps is the schema and the styles the
 *  `flavor` option below selects. Both are required by their licences, and
 *  Leaflet renders this into the attribution control. */
export const BASEMAP_ATTRIBUTION = '&copy; OpenStreetMap &copy; Protomaps';

/**
 * How far the map can be zoomed. NOT optional, and not cosmetic — this line
 * exists because leaving it out took the dispatcher console down.
 *
 * Leaflet's map.getMaxZoom() returns Infinity when no layer on it declares a
 * maximum, and leaflet.markercluster refuses to initialise against that:
 * `throw "Map has no maxZoom specified"` (markercluster src line 605). The
 * raster layer this replaced happened to pass maxZoom: 19, so the cluster
 * group had a finite number to work with and nobody had to know the
 * dependency existed. Dropping it in the rewrite turned LiveMap into an
 * error boundary — a caught render exception, so every other signal stayed
 * green: the API answered, SSE stayed connected, the nav rendered, and the
 * one panel a dispatcher actually works in showed the word "Error".
 *
 * LiveMap is the only map here with a cluster group, which is exactly why
 * only that screen broke while the three order/site maps rendered fine.
 */
export const BASEMAP_MAX_ZOOM = 19;

/**
 * The deepest zoom the archive actually contains, which is a different
 * number from the one above and has to be told to the layer separately.
 *
 * deploy/basemap/prepare.sh builds with --maxzoom=14, because each extra
 * level roughly doubles the file. Past 14 protomaps-leaflet scales what it
 * has instead of asking for tiles that were never built; without this it
 * would request z15-19 and render nothing at close range. Change one of
 * these and change the other.
 */
export const BASEMAP_MAX_DATA_ZOOM = 14;

/**
 * ONE archive handle for the whole app, and the reason is measurable.
 *
 * A .pmtiles archive is not read front to back. Before it can serve a single
 * tile the client fetches the header, then the root directory, then whatever
 * leaf directory covers the area asked for — round trips that all have to
 * finish before anything appears on screen. The `pmtiles` library caches
 * exactly those (its SharedPromiseCache is documented as caching "headers
 * and directories, not individual tile contents"), and that cache lives on
 * the PMTiles INSTANCE.
 *
 * Passing a URL string does not share it. protomaps-leaflet's source does
 * `typeof url === "string" ? new PMTiles(url) : url`, so every string handed
 * to it builds a fresh handle with an empty cache — and the handshake runs
 * again on every mount, every navigation back to the dispatcher tab, and
 * every flip of the day/night toggle. That is felt exactly where it was
 * reported: opening the map, and zooming, both of which need directory
 * lookups before any tile can be drawn. Panning inside a zoom level does
 * not, which is why panning felt fine.
 *
 * Module scope is deliberate: constructing this does no I/O — it only
 * remembers the URL — so the cost is nothing until the first map mounts,
 * and from then on all four maps and every remount share one warm cache.
 */
const ARCHIVE = new PMTiles(BASEMAP_URL);

/** Exposed for the test that asserts every layer gets the SAME handle.
 *  Sharing this is a performance property with no visible symptom when it
 *  breaks — the map still works, just slowly — so it needs an assertion
 *  rather than a comment. */
export function basemapArchive() {
  return ARCHIVE;
}

/**
 * Build the basemap layer. `dark` maps to Protomaps' own light/dark
 * flavors, which is what the dispatcher console's "Diena / naktis" toggle
 * switches between.
 *
 * Unlike the raster layer this replaced, the flavor cannot be swapped in
 * place — there is no setUrl() equivalent, because the theme drives paint
 * rules rather than a URL template (protomaps-leaflet#176). Callers that
 * toggle must remove the old layer and add a new one; LiveMap.jsx does
 * exactly that and is the only caller that needs to.
 */
export function basemapLayer({ dark = true } = {}) {
  return leafletLayer({
    // The shared handle, never the URL string — see ARCHIVE above for what
    // passing a string costs.
    url: ARCHIVE,
    flavor: dark ? 'dark' : 'light',
    attribution: BASEMAP_ATTRIBUTION,
    maxZoom: BASEMAP_MAX_ZOOM,
    maxDataZoom: BASEMAP_MAX_DATA_ZOOM,
    // Labels in Lithuanian where OSM has a name:lt, falling back to the
    // local name — the same rule the rest of this console follows.
    lang: 'lt',
  });
}
